import React, { useCallback, useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { HandbookBlock, HandbookLang, Localized } from '../../api/handbook';
import {
  C,
  FONT,
  PANEL_RADIUS,
  REDUCE_MOTION,
  elbowTabSx,
  hexToRgba,
  lcarsLabelSx,
  prefersReducedMotion,
} from '../../theme/tokens';

const say = (v: Localized, lang: HandbookLang) => v[lang];

/** Prose measure. Longer than this and the eye loses the line return; the
 * handbook is read start to finish, not scanned. */
const MEASURE = '68ch';

/** Status words the source document uses as a traffic light. When one is the
 * first cell of a table row, the row carries its colour — the meaning is the
 * whole point of that table and a monochrome rendering throws it away. */
const SIGNAL: Record<string, string> = {
  'GRÜN': C.green,
  GREEN: C.green,
  GELB: C.gold,
  YELLOW: C.gold,
  ROT: C.red,
  RED: C.red,
  SCHWARZ: C.textDim,
  BLACK: C.textDim,
};

/**
 * The document's ASCII, centred in its box.
 *
 * `width: fit-content` plus auto margins centres the block *as a block*, which
 * is the only correct way to do it here: `text-align: center` would centre
 * each line independently and shear every box-drawing diagram apart. When a
 * diagram is wider than its container, `fit-content` exceeds the box, the auto
 * margins collapse to zero, and the parent's `overflow-x: auto` scrolls it
 * from the left edge as before.
 */
const asciiBlockSx = {
  m: 0,
  mx: 'auto',
  width: 'fit-content',
  minWidth: 0,
  fontFamily: FONT.mono,
  fontSize: { xs: '0.68rem', md: '0.76rem', xl: '0.88rem' },
  lineHeight: 1.7,
  color: hexToRgba(C.cyan, 0.92),
  whiteSpace: 'pre',
} as const;

const Prose: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography
    sx={{
      fontFamily: FONT.body,
      fontSize: { xs: '0.95rem', xl: '1.06rem' },
      lineHeight: 1.72,
      color: C.text,
      maxWidth: MEASURE,
    }}
  >
    {children}
  </Typography>
);

/** Bullets and steps share a row shell so their rhythm matches; only the
 * marker differs — a chevron for an unordered rule, a mono index for a step
 * whose order is load-bearing. */
const Row: React.FC<{ marker: React.ReactNode; children: React.ReactNode }> = ({ marker, children }) => (
  <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
    <Box sx={{ flexShrink: 0, mt: '0.28em', width: 18, display: 'flex', justifyContent: 'flex-start' }}>{marker}</Box>
    <Typography sx={{ fontFamily: FONT.body, fontSize: { xs: '0.93rem', xl: '1.04rem' }, lineHeight: 1.66, color: C.text, maxWidth: MEASURE }}>
      {children}
    </Typography>
  </Box>
);

const Chevron: React.FC = () => (
  <Box
    component="svg"
    viewBox="0 0 8 10"
    aria-hidden
    sx={{ width: 7, height: 9, overflow: 'visible' }}
  >
    <path d="M1 1L6 5L1 9" fill="none" stroke={C.cyan} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
  </Box>
);

/**
 * A diagram that is really a sequence — a comms exchange, a bookmark set, a
 * response flow — played out one line at a time the first time it is read,
 * then held. It runs once and stops: a loop next to running prose is a
 * distraction, and this is a handbook, not a title screen.
 *
 * The delay is per line and capped, so a five-line call-and-response and a
 * nine-line checklist both finish inside a beat.
 */
const LineReveal: React.FC<{ body: string }> = ({ body }) => {
  const lines = body.split('\n');

  return (
    <Box component="pre" sx={asciiBlockSx}>
      {lines.map((line, i) => (
        <Box
          component="span"
          key={i}
          sx={{
            display: 'block',
            // Left-aligned *within* the block. Only the block as a whole is
            // centred (see `asciiBlockSx`) — centring each line would shear
            // the box-drawing apart.
            textAlign: 'left',
            // Visible is the default state, and the animation is the
            // enhancement layered on top — never the other way round. A first
            // pass gated this on a per-block IntersectionObserver that was
            // measured never firing, and it shipped blank boxes where SOP
            // diagrams belong. Content a member reads under time pressure
            // does not get to depend on an effect running.
            opacity: 1,
            // `backwards` holds the from-state through the delay, so the
            // stagger reads as a sequence; with no `[data-in]` ancestor (or
            // under reduced motion) no animation runs at all and the line
            // simply stays visible.
            '[data-in="true"] &': {
              animation: `handbookLineIn 0.2s ease-out ${i * 42}ms backwards`,
            },
            '@keyframes handbookLineIn': {
              from: { opacity: 0 },
              to: { opacity: 1 },
            },
            [REDUCE_MOTION]: { animation: 'none' },
          }}
        >
          {line || ' '}
        </Box>
      ))}
    </Box>
  );
};

const Diagram: React.FC<{
  caption: string | null;
  body: string;
  animate?: 'lines';
}> = ({ caption, body, animate }) => (
  <Box>
    {caption && <Typography sx={{ ...lcarsLabelSx(C.cyan, '0.68rem'), mb: 0.75 }}>{caption}</Typography>}
    <Box
      sx={{
        // The document's own ASCII, kept as-is. It is measurement and
        // structure, which is exactly what this face is for.
        backgroundColor: hexToRgba(C.wormhole, 0.05),
        border: `1px solid ${C.hairline}`,
        borderRadius: `${PANEL_RADIUS}px`,
        px: { xs: 1.5, md: 2 },
        py: { xs: 1.25, md: 1.5 },
        overflowX: 'auto',
      }}
    >
      {animate === 'lines' ? (
        <LineReveal body={body} />
      ) : (
        <Box component="pre" sx={asciiBlockSx}>
          {body}
        </Box>
      )}
    </Box>
  </Box>
);

const Callout: React.FC<{ title: string; body: string }> = ({ title, body }) => (
  <Box sx={{ maxWidth: MEASURE }}>
    {/* The one sanctioned LCARS shape, on the one element that earns it: a
        rule the handbook itself sets apart from its prose. */}
    <Typography component="span" sx={elbowTabSx(C.wormhole)}>
      {title}
    </Typography>
    <Box
      sx={{
        backgroundColor: hexToRgba(C.wormhole, 0.08),
        border: `1px solid ${hexToRgba(C.wormhole, 0.32)}`,
        borderTopLeftRadius: 0,
        borderRadius: `0 ${PANEL_RADIUS}px ${PANEL_RADIUS}px ${PANEL_RADIUS}px`,
        px: { xs: 1.75, md: 2.25 },
        py: { xs: 1.5, md: 1.75 },
      }}
    >
      <Typography sx={{ fontFamily: FONT.body, fontSize: { xs: '0.95rem', xl: '1.06rem' }, lineHeight: 1.7, color: C.textBright }}>
        {body}
      </Typography>
    </Box>
  </Box>
);

const Table: React.FC<{ head: string[]; rows: string[][] }> = ({ head, rows }) => (
  <Box sx={{ overflowX: 'auto', maxWidth: '100%' }}>
    <Box
      component="table"
      sx={{
        width: '100%',
        minWidth: head.length > 2 ? 560 : 360,
        borderCollapse: 'collapse',
        fontFamily: FONT.body,
      }}
    >
      <Box component="thead">
        <Box component="tr">
          {head.map((h, i) => (
            <Box
              component="th"
              key={i}
              sx={{
                ...lcarsLabelSx(C.cyan, '0.68rem'),
                textAlign: 'left',
                py: 0.75,
                pr: 2,
                borderBottom: `1px solid ${hexToRgba(C.cyan, 0.3)}`,
                whiteSpace: 'nowrap',
              }}
            >
              {h}
            </Box>
          ))}
        </Box>
      </Box>
      <Box component="tbody">
        {rows.map((r, ri) => {
          const signal = SIGNAL[r[0]?.trim() ?? ''];
          return (
            <Box component="tr" key={ri}>
              {r.map((cell, ci) => (
                <Box
                  component="td"
                  key={ci}
                  sx={{
                    py: 1.1,
                    pr: 2,
                    verticalAlign: 'top',
                    borderBottom: `1px solid ${C.hairline}`,
                    fontSize: { xs: '0.88rem', xl: '0.98rem' },
                    lineHeight: 1.55,
                    color: ci === 0 ? (signal ?? C.textBright) : C.text,
                    fontFamily: ci === 0 && signal ? FONT.sub : FONT.body,
                    fontWeight: ci === 0 ? (signal ? 700 : 600) : 400,
                    letterSpacing: ci === 0 && signal ? '0.1em' : undefined,
                  }}
                >
                  {cell}
                </Box>
              ))}
            </Box>
          );
        })}
      </Box>
    </Box>
  </Box>
);

const STORE_PREFIX = 'gobbo.handbook.check.';

/**
 * The document's `□` lists, made to actually work.
 *
 * A pre-PvE check and a licence level are things a member ticks off, so they
 * tick off — kept in this browser only. Private storage is the honest scope:
 * a licence is signed off by a person, not by a checkbox, and nothing here
 * claims otherwise.
 */
const Checklist: React.FC<{ id: string; items: string[]; note?: string }> = ({ id, items, note }) => {
  const key = STORE_PREFIX + id;
  const [ticked, setTicked] = useState<boolean[]>(() => items.map(() => false));

  useEffect(() => {
    try {
      const raw = window.localStorage.getItem(key);
      if (raw) {
        const saved = JSON.parse(raw) as boolean[];
        setTicked(items.map((_, i) => saved[i] === true));
      }
    } catch {
      /* private mode, blocked storage: an unticked list is a valid start */
    }
    // Items are static per checklist id; re-reading on every render would
    // fight the user's own clicks.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  const toggle = useCallback(
    (i: number) => {
      setTicked((prev) => {
        const next = prev.map((v, n) => (n === i ? !v : v));
        try {
          window.localStorage.setItem(key, JSON.stringify(next));
        } catch {
          /* nothing to persist to; the list still works for this session */
        }
        return next;
      });
    },
    [key],
  );

  const done = ticked.filter(Boolean).length;
  const complete = done === items.length;

  return (
    <Box sx={{ maxWidth: MEASURE }}>
      {note && (
        <Typography
          sx={{ fontFamily: FONT.body, fontSize: { xs: '0.88rem', xl: '0.98rem' }, lineHeight: 1.6, color: C.textDim, mb: 1.75 }}
        >
          {note}
        </Typography>
      )}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 1.25 }}>
        <Box
          sx={{
            flex: 1,
            height: 3,
            borderRadius: 999,
            backgroundColor: hexToRgba(C.text, 0.12),
            overflow: 'hidden',
          }}
        >
          {/* Full width, scaled from the left edge — animating `width` would
              lay out the row on every frame of a tick. */}
          <Box
            sx={{
              height: '100%',
              width: '100%',
              transformOrigin: 'left center',
              transform: `scaleX(${done / items.length})`,
              borderRadius: 999,
              backgroundColor: complete ? C.green : C.cyan,
              boxShadow: `0 0 8px ${hexToRgba(complete ? C.green : C.cyan, 0.6)}`,
              transition: prefersReducedMotion()
                ? 'background-color 0.3s ease'
                : 'transform 0.3s cubic-bezier(0.16,1,0.3,1), background-color 0.3s ease',
            }}
          />
        </Box>
        <Typography sx={{ fontFamily: FONT.mono, fontSize: '0.72rem', color: complete ? C.green : C.textDim }}>
          {done}/{items.length}
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
        {items.map((item, i) => {
          const on = ticked[i];
          return (
            <Box
              key={i}
              component="button"
              type="button"
              aria-pressed={on}
              onClick={() => toggle(i)}
              sx={{
                appearance: 'none',
                background: 'none',
                border: 'none',
                textAlign: 'left',
                width: '100%',
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1.25,
                px: 1,
                py: 0.85,
                borderRadius: '6px',
                cursor: 'pointer',
                transition: 'background-color 0.18s ease',
                '&:hover': { backgroundColor: hexToRgba(C.cyan, 0.06) },
                '&:focus-visible': { outline: `2px solid ${C.cyan}`, outlineOffset: '-2px' },
              }}
            >
              <Box
                aria-hidden
                sx={{
                  flexShrink: 0,
                  mt: '0.16em',
                  width: 14,
                  height: 14,
                  borderRadius: '3px',
                  border: `1px solid ${on ? C.green : hexToRgba(C.cyan, 0.45)}`,
                  backgroundColor: on ? hexToRgba(C.green, 0.9) : 'transparent',
                  boxShadow: on ? `0 0 9px ${hexToRgba(C.green, 0.5)}` : 'none',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'background-color 0.18s ease, border-color 0.18s ease',
                }}
              >
                {on && (
                  <Box component="svg" viewBox="0 0 10 8" sx={{ width: 8, height: 6.5 }}>
                    <path
                      d="M1 4L3.6 6.5L9 1"
                      fill="none"
                      stroke={C.bg}
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </Box>
                )}
              </Box>
              <Typography
                sx={{
                  fontFamily: FONT.body,
                  fontSize: '0.9rem',
                  lineHeight: 1.55,
                  color: on ? C.textDim : C.text,
                  textDecoration: on ? 'line-through' : 'none',
                  textDecorationColor: hexToRgba(C.green, 0.55),
                  transition: 'color 0.18s ease',
                }}
              >
                {item}
              </Typography>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

const Links: React.FC<{ items: { label: string; href: string }[] }> = ({ items }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, maxWidth: MEASURE }}>
    {items.map((l) => (
      <Box
        key={l.href}
        component="a"
        href={l.href}
        target="_blank"
        rel="noopener noreferrer"
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'baseline',
          gap: 1,
          py: 0.75,
          px: 1,
          ml: -1,
          borderRadius: '6px',
          textDecoration: 'none',
          color: C.text,
          fontFamily: FONT.body,
          fontSize: '0.9rem',
          transition: 'background-color 0.18s ease, color 0.18s ease',
          '&:hover': { backgroundColor: hexToRgba(C.cyan, 0.06), color: C.textBright },
          '&:focus-visible': { outline: `2px solid ${C.cyan}`, outlineOffset: '-2px' },
        }}
      >
        <Box component="span">{l.label}</Box>
        <Box component="span" sx={{ fontFamily: FONT.mono, fontSize: '0.7rem', color: hexToRgba(C.cyan, 0.75) }}>
          {new URL(l.href).hostname.replace(/^www\./, '')}
        </Box>
      </Box>
    ))}
  </Box>
);

const BlockView: React.FC<{ block: HandbookBlock; lang: HandbookLang }> = ({ block, lang }) => {
  switch (block.kind) {
    case 'text':
      return <Prose>{say(block.body, lang)}</Prose>;

    case 'bullets':
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {block.items.map((it, i) => (
            <Row key={i} marker={<Chevron />}>
              {say(it, lang)}
            </Row>
          ))}
        </Box>
      );

    case 'steps':
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {block.items.map((it, i) => (
            <Row
              key={i}
              marker={
                <Typography sx={{ fontFamily: FONT.mono, fontSize: '0.76rem', color: C.cyan, lineHeight: 1 }}>
                  {String(i + 1).padStart(2, '0')}
                </Typography>
              }
            >
              {say(it, lang)}
            </Row>
          ))}
        </Box>
      );

    case 'table':
      return (
        <Table
          head={block.head.map((h) => say(h, lang))}
          rows={block.rows.map((r) => r.map((c) => say(c, lang)))}
        />
      );

    case 'callout':
      return <Callout title={say(block.title, lang)} body={say(block.body, lang)} />;

    case 'diagram':
      return (
        <Diagram
          caption={block.caption ? say(block.caption, lang) : null}
          body={say(block.body, lang)}
          animate={block.animate}
          // The language toggle swaps the ASCII wholesale; remounting is what
          // re-arms a line reveal that already played in the other language.
          key={lang}
        />
      );

    case 'checklist':
      return (
        <Checklist
          id={block.id}
          items={block.items.map((it) => say(it, lang))}
          note={block.note ? say(block.note, lang) : undefined}
        />
      );

    case 'links':
      return <Links items={block.items} />;

    default:
      return null;
  }
};

export default BlockView;
